import { useCallback } from "react";
import type { FormStore } from "../../store/types";
import { useFormStoreContext } from "../../store/zustand-context";
import { prepareFieldValuesForSubmission } from "../../utils/field-collection-utils";
import type { UseFormOptions, UseFormResult } from "./types";
import { useFormDynamicFields } from "./useFormDynamicFields";
import { useFormInitialize } from "./useFormInitialize";
import { useFormValidation } from "./useFormValidation";
import { useFormValues } from "./useFormValues";

export function useForm(options?: UseFormOptions): UseFormResult {
  const { formDefinition, initialValues, onSubmit } = options || {};
  const store: FormStore = useFormStoreContext();

  // 初期化処理を分離したフック
  const { initialized } = useFormInitialize({
    store,
    formDefinition,
    initialValues,
  });

  // バリデーション処理を分離したフック
  const {
    validateField: validateFieldFn,
    validateAll,
    errors,
    hasErrors,
  } = useFormValidation({ store });

  // 値の管理処理を分離したフック
  const { setValue, setValues, values } = useFormValues({
    store,
    validateField: validateFieldFn,
    validateAll,
  });

  // 動的フィールド管理処理を分離したフック
  const { getDynamicFieldGroup, getDynamicCollections, getDynamicFields } =
    useFormDynamicFields({ store });

  // フォームの値をリセットする処理
  const reset = useCallback(() => {
    store.reset();
  }, [store]);

  // 送信処理
  const submit = useCallback(async () => {
    store.setIsSubmitAttempted(true);
    const isValid = await validateAll();
    if (!isValid) {
      return false;
    }
    store.setIsSubmitting(true);
    if (typeof onSubmit === "function") {
      try {
        // 送信前に値を変換（{value, label}オブジェクトからvalue部分を抽出）
        const preparedValues = prepareFieldValuesForSubmission(store.values);
        await onSubmit(preparedValues);
        await store.submit(() => Promise.resolve());
        store.setIsSubmitting(false);
        return true;
      } catch (_error) {
        store.setIsSubmitting(false);
        return false;
      }
    } else {
      return false;
    }
  }, [store, onSubmit, validateAll]);

  return {
    initialized,
    values,
    errors,
    setValue,
    setValues,
    reset,
    validateField: validateFieldFn,
    validateAll,
    submit,
    hasErrors,
    isSubmitting: store.isSubmitting,
    isSubmitted: store.isSubmitted,
    isSubmitAttempted: store.isSubmitAttempted,
    getDynamicFieldGroup,
    getDynamicCollections,
    getDynamicFields,
  };
}
